var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { Dropdown, Icon, Input } from '..';
import { useTheme } from '../../theme';
import { useMultipleMemoClasses, useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    base: {
        position: 'relative',
        font: 'var(--RUI-font-input)',
        margin: 'var(--RUI-margin)',
    },
    baseOverrides: function (theme) { return (__assign({}, theme.overrides.Select.base)); },
    icon: {
        position: 'absolute',
        right: 0,
        bottom: 'calc(0.3125 * var(--RUI-padding))',
        cursor: 'pointer',
        fontSize: '1.5em',
    },
    iconOverrides: function (theme) { return (__assign({}, theme.overrides.Select.icon)); },
    options: {
        font: 'var(--RUI-font-input)',
        marginTop: '5px',
        backgroundColor: 'var(--RUI-color-paper)',
        border: 'var(--RUI-border-width) solid black',
        boxShadow: '2px 2px 8px -4px black',
    },
    optionsOverrides: function (theme) { return (__assign({}, theme.overrides.Select.options)); },
    option: {
        cursor: 'pointer',
        padding: 'calc(0.7 * var(--RUI-padding)) calc(1 * var(--RUI-padding))',
        '&:hover': {
            backgroundColor: 'var(--RUI-color-paper-darker)',
            color: 'var(--RUI-color-paper-contrast)',
        },
        '&:focus': {
            backgroundColor: 'var(--RUI-select-color)',
            color: 'var(--RUI-select-contrast)',
        },
    },
    optionOverrides: function (theme) { return (__assign({}, theme.overrides.Select.option)); },
    primary: {
        '--RUI-select-color': 'var(--RUI-color-primary-light)',
        '--RUI-select-contrast': 'var(--RUI-color-primary-contrast)',
    },
    secondary: {
        '--RUI-select-color': 'var(--RUI-color-secondary-light)',
        '--RUI-select-contrast': 'var(--RUI-color-secondary-contrast)',
    },
    neutral: {
        '--RUI-select-color': 'var(--RUI-color-neutral-light)',
        '--RUI-select-contrast': 'var(--RUI-color-neutral-contrast)',
    },
    error: {
        '--RUI-select-color': 'var(--RUI-color-error-light)',
        '--RUI-select-contrast': 'var(--RUI-color-error-contrast)',
    },
    success: {
        '--RUI-select-color': 'var(--RUI-color-success-light)',
        '--RUI-select-contrast': 'var(--RUI-color-success-contrast)',
    },
    warning: {
        '--RUI-select-color': 'var(--RUI-color-warning-light)',
        '--RUI-select-contrast': 'var(--RUI-color-warning-contrast)',
    },
}, { name: 'RUISelect' });
var Select = function (_a) {
    var _b;
    var value = _a.value, onChange = _a.onChange, options = _a.options, color = _a.color, closedIcon = _a.closedIcon, style = _a.style, openedIcon = _a.openedIcon, className = _a.className, defaultValue = _a.defaultValue, props = __rest(_a, ["value", "onChange", "options", "color", "closedIcon", "style", "openedIcon", "className", "defaultValue"]);
    var theme = useTheme();
    var styles = useStyles(theme);
    var _c = useState(false), open = _c[0], setOpen = _c[1];
    var _d = useState(value || defaultValue || ''), innerValue = _d[0], setInnerValue = _d[1];
    var _e = useState(function () {
        var initIndex = options.findIndex(function (option) { return option.value === innerValue; });
        return initIndex !== -1 ? initIndex + 1 : 0;
    }), index = _e[0], setIndex = _e[1];
    // if external value and internalIndex don't match, always default to external value
    var externalIndex = options.findIndex(function (o) { return o.value === value; }) + 1;
    if (externalIndex !== 0 && externalIndex !== index)
        setIndex(externalIndex);
    // when updating index, ignore a change to index 0 because this is the default empty option
    var updateIndex = function (index) {
        setIndex(index === 0 ? 1 : index);
    };
    var optionsClasses = useMemoClasses([styles.options, theme.overrides.Select.options && styles.optionsOverrides, styles[color], 'RUISelect-options'], [theme, color]);
    var _f = useMultipleMemoClasses([
        [styles.base, theme.overrides.Select.base && styles.baseOverrides, className, 'RUISelect-base'],
        [styles.icon, theme.overrides.Select.icon && styles.iconOverrides, 'RUISelect-icon'],
        [styles.option, theme.overrides.Select.option && styles.optionOverrides, 'RUISelect-option'],
    ], [theme]), baseClasses = _f[0], iconClasses = _f[1], optionClasses = _f[2];
    useEffect(function () {
        setInnerValue(value);
        setIndex(0);
    }, [value]);
    var handleOpenToggle = function (open, e) {
        var _a;
        // toggles open state to update Input active look
        setOpen(open);
        // update new value when select closes
        if (!open) {
            // UI events are clicks outside window
            // React.UIEvents are clicks and keydowns inside component
            if (e instanceof UIEvent || e.type === 'keydown') {
                // close due to click outside or keyboard should update value with current index
                var newValue = index !== 0 ? options.find(function (_, i) { return i === index - 1; }).value : '';
                onChange(e, newValue);
                setInnerValue(newValue);
            }
            else {
                // clicks inside should check the value of the clicked item and update both value & index
                var newValue_1 = (_a = e.target) === null || _a === void 0 ? void 0 : _a.getAttribute('data-value');
                setIndex(options.findIndex(function (option) { return option.value === newValue_1; }) + 1); // correct for default option at index 0
                onChange(e, newValue_1);
                setInnerValue(newValue_1);
            }
        }
    };
    // external value can be undefined but still falsy (""), only use innerValue if not undefined
    var displayValue = value !== undefined ? value : innerValue;
    // base has an extra input with true value for form submisison purposes
    // custom Input component has value set to option.label to show correct text
    var base = (React.createElement("div", { style: style, className: baseClasses },
        React.createElement(Input, __assign({ style: { margin: 0 }, readOnly: true, active: open, color: color, type: "text", value: ((_b = options.find(function (_, i) { return i === index - 1; })) === null || _b === void 0 ? void 0 : _b.label) || '' }, props)),
        React.createElement(Icon, { className: iconClasses }, open ? openedIcon : closedIcon),
        React.createElement("input", { value: displayValue, hidden: true, readOnly: true })));
    // window has a defualt option that allows menu to focus item inside without selecting a value
    // neither `visibility: hidden` nor `diplay: none` can be used because they block focus
    var window = options.length ? (React.createElement("div", { className: optionsClasses },
        React.createElement("div", { style: { opacity: '0', width: '0', height: '0' }, tabIndex: -1, "data-value": "" }, "default"),
        options.map(function (option) { return (React.createElement("div", { className: optionClasses, key: option.value, tabIndex: 0, "data-value": option.value }, option.label)); }))) : null;
    return (React.createElement(Dropdown, { baseNode: base, windowNode: window, mode: ['keyboard', 'click'], onOpenToggle: handleOpenToggle, index: index, onIndexChange: updateIndex }));
};
Select.defaultProps = {
    color: 'primary',
    options: [],
    onChange: function () { },
    closedIcon: 'arrow_drop_down',
    openedIcon: 'arrow_drop_up',
};
export { Select as default, Select };
